import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, UploadCloud, GitBranch } from "lucide-react";
import { WIZARD_STEPS as steps } from "../utils/api";

const currentStep = 0;

const METHODS = [
  {
    id: "upload",
    title: "Manual File Upload",
    icon: UploadCloud,
    description: "Upload an artifact once and walk through the signing wizard yourself — upload, manifest, identity, review, sign.",
    bullets: [
      "Best for one-off signing or testing",
      "Full step-by-step review before signing",
    ],
    route: "/upload-artifact",
  },
  {
    id: "cicd",
    title: "CI/CD Pipeline Integration",
    icon: GitBranch,
    description: "Get ready-to-use Jenkins, GitLab CI, GitHub Actions, or Azure DevOps snippets that call the real signing API automatically on every build.",
    bullets: [
      "Best for automated, repeatable signing",
      "Uses the same REST endpoints as this wizard",
    ],
    route: "/ci-cd-setup",
  },
];

export default function ChooseSigningMethod() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);

  const handleNext = () => {
    const method = METHODS.find((m) => m.id === selected);
    if (!method) {
      alert("Please choose a signing method.");
      return;
    }
    navigate(method.route);
  };

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>

      {/* STEPPER */}
      <div className="flex items-center justify-between relative mb-5" style={{ height: "72px" }}>
        {steps.map((step, index) => {
          const isDone = index < currentStep;
          const isCurrent = index === currentStep;

          let circleClass = "bg-gray-200 text-gray-600";
          let labelClass = "text-gray-500";
          if (isDone) {
            circleClass = "bg-green-500 text-white";
            labelClass = "text-green-600";
          } else if (isCurrent) {
            circleClass = "bg-blue-600 text-white";
            labelClass = "text-blue-600";
          }

          return (
          <div key={step} className="flex-1 flex flex-col items-center relative">
            {index !== steps.length - 1 && (
              <div className={`absolute top-5 left-1/2 w-full h-[2px] ${index < currentStep ? "bg-green-500" : "bg-gray-300"}`} />
            )}
            <div
              className={`z-10 rounded-full flex items-center justify-center text-xs font-semibold ${circleClass}`}
              style={{ width: "40px", height: "40px" }}
            >
              {index < currentStep ? <Check size={14} /> : index + 1}
            </div>
            <p className={`mt-2 text-[14px] font-medium text-center ${labelClass}`}>
              {step}
            </p>
          </div>
          );
        })}
      </div>

      <p style={{ fontSize: "24px", fontWeight: 700, color: "#1F2937" }} className="mb-1">Choose Signing Method</p>
      <p className="text-sm text-gray-400 mb-5">Choose how you'd like to sign artifacts. You can always use the other method later.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
        {METHODS.map((method) => {
          const Icon = method.icon;
          const isSelected = selected === method.id;
          return (
            <div
              key={method.id}
              onClick={() => setSelected(method.id)}
              className={`p-5 rounded-2xl border-2 cursor-pointer transition bg-white ${
                isSelected ? "border-blue-500" : "border-gray-200 hover:border-blue-300"
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center"
                  style={{ background: isSelected ? "#DBEAFE" : "#F1F5F9" }}
                >
                  <Icon size={20} color={isSelected ? "#2563EB" : "#64748B"} />
                </div>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? "border-blue-600 bg-blue-600" : "border-gray-300"}`}>
                  {isSelected && <Check size={12} className="text-white" />}
                </div>
              </div>
              <p className="text-[16px] font-semibold text-gray-800 mb-1">{method.title}</p>
              <p className="text-[13px] text-gray-400 mb-3 leading-relaxed">{method.description}</p>
              <ul className="space-y-1">
                {method.bullets.map((b) => (
                  <li key={b} className="text-[12.5px] text-gray-500 flex items-start gap-1.5">
                    <span className="text-blue-500 mt-0.5">•</span> {b}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <div className="flex justify-end mt-6">
        <button
          onClick={handleNext}
          className="text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition rounded-[10px]"
          style={{ height: "44px", padding: "0 24px" }}
        >
          Next
        </button>
      </div>
    </div>
  );
}